export type ExceptionResolution =
	| "explained"
	| "db_corrected"
	| "stripe_only"
	| "write_off"

export interface ResolutionOption {
	value: ExceptionResolution
	label: string
	/** When to pick this resolution, in plain words. */
	when: string
	/** What happens to the statement and the close once it is picked. */
	effect: string
}

/** The four ways to explain an exception, in the order they are offered. */
export const RESOLUTION_OPTIONS: readonly ResolutionOption[] = [
	{
		value: "explained",
		label: "Explained, nothing to change",
		when: "The movement is legitimate and only needed a note: a top-up, a chargeback, a refund issued by hand for a known reason, a movement Stripe posted a day late.",
		effect:
			"Figures stay as they are. The exception stops blocking the month and your note is printed in the workbook's exceptions list.",
	},
	{
		value: "db_corrected",
		label: "Corrected in the database",
		when: "The missing or wrong fact has been fixed on our side: a refund recorded on the order, a charge id filled in, a transfer recorded. Write what was changed and by whom.",
		effect:
			"The next sync re-matches the row against the corrected fact and moves the exception to resolved on its own; your note is kept.",
	},
	{
		value: "stripe_only",
		label: "Stripe only, no counterpart in our database",
		when: "The movement will never have a database side: a test payment, a payment link, a transfer made by hand for a reason outside the marketplace.",
		effect:
			"The row stays in the statement as a Stripe-only line with your note; nothing is expected from the database.",
	},
	{
		value: "write_off",
		label: "Write-off, difference too small to chase",
		when: "A residual difference that is not worth resolving, such as a one-cent rounding between a payout and the order's own arithmetic.",
		effect:
			"The exception stays in the statement's exceptions list marked as a write-off with your note, and the month can be finalised.",
	},
]

export const RESOLUTION_LABELS: Record<ExceptionResolution, string> =
	Object.fromEntries(
		RESOLUTION_OPTIONS.map((option) => [option.value, option.label]),
	) as Record<ExceptionResolution, string>

export const EXCEPTION_STATUS_LABELS: Record<string, string> = {
	open: "Open, blocks the month",
	explained: "Explained",
	resolved: "Resolved by a later sync",
}

export interface ExceptionKindGuidance {
	/** What the exception means, in one sentence. */
	meaning: string
	/** What usually explains it and where to look. */
	check: string
	/** The resolution to preselect when the exception has none yet. */
	suggested: ExceptionResolution
	/** An example note, shown as the placeholder. */
	example: string
}

/** Plain-words guidance per exception kind, shown in the annotate dialog. */
export const EXCEPTION_KIND_GUIDANCE: Record<string, ExceptionKindGuidance> = {
	unmatched_bt: {
		meaning:
			"Stripe moved money that we cannot tie to any transaction, order, refund or payout in our database.",
		check:
			"Open the Stripe link. Empty metadata means the movement was created outside the marketplace (Dashboard, payment link, test). A charge or transfer created by hand is Stripe only; a refund made by hand for a known order is explained with the reason.",
		suggested: "stripe_only",
		example:
			"Manual transfer to reimburse a seller's shipping label, agreed on Discord; no order.",
	},
	partial_match: {
		meaning:
			"The movement is tied to a transaction or order, but a detail is missing on our side (a fee, a refund entry, a charge id) or the refund was issued from the Stripe Dashboard.",
		check:
			"Read the detection note above. When the fact can be recorded on our side, do it and pick Corrected in the database; when the refund was issued by hand, pick Explained and write why it was issued.",
		suggested: "explained",
		example:
			"Goodwill refund issued from the Stripe Dashboard on 25 May after the buyer's complaint; order OR-…",
	},
	amount_mismatch: {
		meaning:
			"The movement matches a fact in our database, but the amounts differ.",
		check:
			"Compare the Stripe amount with the order's own figure. A cent of rounding is a write-off; a larger gap is a fact to correct on our side or a movement to explain.",
		suggested: "write_off",
		example: "One-cent rounding between the payout and the order's fee split.",
	},
	unclassified_bt: {
		meaning:
			"Stripe posted a movement of a type the ledger does not model yet.",
		check:
			"Open the Stripe link and note what the movement is. Tell the engineering team the Stripe type so it gets a category; until then pick Explained with the description.",
		suggested: "explained",
		example:
			"Connect reserve held on 24 March and released on 25 March for the OR-… incident.",
	},
	treasury_movement: {
		meaning:
			"A top-up into the platform balance or a payout to the bank account, which never has a counterpart in the marketplace.",
		check:
			"Say what the money was for: funding the payments balance, funding the refund and dispute reserve, or a payout to the company bank account.",
		suggested: "explained",
		example: "Top-up of the payments balance decided with the CFO.",
	},
	charge_without_bt: {
		meaning:
			"Our database has a paid transaction, but Stripe shows no matching charge yet.",
		check:
			"A movement posted after the month end is ingested by the next sync; wait for it before annotating. If it never appears, check the transaction's Stripe reference on its page.",
		suggested: "explained",
		example:
			"Charge posted by Stripe on 1 October for a payment of 30 September; ingested by the next sync.",
	},
	refund_without_bt: {
		meaning: "Our database records a refund that Stripe has not posted.",
		check:
			"Wait for the next sync when the refund is recent. If it never appears, the refund may have failed on Stripe: check the refund on the transaction page and correct the entry.",
		suggested: "db_corrected",
		example: "Refund failed on Stripe; entry removed from the transaction.",
	},
	balance_refund_without_bt: {
		meaning:
			"Our database records a refund of a balance payment (a transfer back to the buyer's seller account) that Stripe has not posted.",
		check:
			"Wait for the next sync when the refund is recent; otherwise check the transfer on Stripe from the transaction page.",
		suggested: "db_corrected",
		example: "Balance refund transfer failed on Stripe; entry corrected.",
	},
	transfer_without_bt: {
		meaning: "Our database records a seller payout that Stripe has not posted.",
		check:
			"Wait for the next sync when the payout is recent; otherwise open the order's transfer id on Stripe and check whether the transfer exists.",
		suggested: "db_corrected",
		example:
			"Transfer recorded on the order but never created on Stripe; payout re-issued.",
	},
	dispute_without_bt: {
		meaning: "Our database records a dispute that has no movement on Stripe.",
		check:
			"An inquiry withdraws no money and never gets a movement: it is explained as such. A real chargeback appears with the next sync.",
		suggested: "explained",
		example: "Inquiry only, no funds withdrawn by Stripe.",
	},
	duplicate_match: {
		meaning: "Two Stripe movements matched the same fact in our database.",
		check:
			"Open both Stripe links. This usually means a duplicated charge or transfer on Stripe; tell the engineering team and explain which one is the real movement.",
		suggested: "explained",
		example: "Second transfer created by mistake and reversed the same day.",
	},
}

/** The guidance of a kind, with a neutral fallback for kinds added later. */
export function exceptionKindGuidance(kind: string): ExceptionKindGuidance {
	return (
		EXCEPTION_KIND_GUIDANCE[kind] ?? {
			meaning: "This movement could not be reconciled automatically.",
			check:
				"Open the Stripe link and the transaction or order, then write what the movement is and how it is booked.",
			suggested: "explained",
			example: "What this movement is and how it is booked.",
		}
	)
}
